import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { API, inr } from '../lib/api';
import { compare } from '../lib/storage';
import { useData } from '../context/DataContext';
import { X, ArrowLeft } from 'lucide-react';

const ComparePage = () => {
  const { supplierById } = useData();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);

  const refresh = async () => {
    const ids = compare.list();
    if (!ids.length) { setProducts([]); return; }
    const r = await axios.post(`${API}/products/batch`, ids).catch(() => ({ data: [] }));
    setProducts(r.data || []);
  };

  useEffect(() => { refresh(); }, []);

  const remove = (id) => { compare.toggle(id); refresh(); };

  if (products.length === 0) {
    return (
      <div>
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold mb-2">Compare Products</h1>
          <p className="text-gray-600 mb-4">Add up to 4 products to compare. Click "Compare" on any product card to add it here.</p>
          <Button onClick={() => navigate('/')} className="bg-[#ee5e23] hover:bg-[#d24e15] text-white"><ArrowLeft className="w-4 h-4 mr-2" /> Browse Products</Button>
        </div>
        <Footer />
      </div>
    );
  }

  // Build comparison rows from union of spec keys
  const allKeys = new Set();
  products.forEach((p) => Object.keys(p.specs || {}).forEach((k) => allKeys.add(k)));

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold">Compare ({products.length})</h1>
          <Button variant="outline" onClick={() => { compare.clear(); refresh(); }}>Clear All</Button>
        </div>
        <div className="bg-white border border-gray-200 rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="p-3 text-left w-40 bg-gray-50"></th>
                {products.map((p) => (
                  <th key={p.id} className="p-3 text-left min-w-[200px]">
                    <button onClick={() => remove(p.id)} className="float-right text-gray-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                    <img src={p.image} alt="" className="w-20 h-20 object-cover rounded mb-2" />
                    <Link to={`/product/${p.slug || p.id}`} className="font-semibold text-gray-900 hover:text-[#ee5e23] block">{p.name}</Link>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-b"><td className="p-3 bg-gray-50 font-medium">Price</td>{products.map((p) => <td key={p.id} className="p-3"><span className="text-[#2c54b2] font-bold">{inr(p.price)}</span> / {p.unit}</td>)}</tr>
              <tr className="border-b"><td className="p-3 bg-gray-50 font-medium">MOQ</td>{products.map((p) => <td key={p.id} className="p-3">{p.moq}</td>)}</tr>
              <tr className="border-b"><td className="p-3 bg-gray-50 font-medium">Vendor</td>{products.map((p) => { const s = supplierById?.[p.supplier_id]; return <td key={p.id} className="p-3">{s ? <Link to={`/vendor/${s.slug || s.id}`} className="text-[#2c54b2] hover:underline">{s.name}</Link> : '—'}</td>; })}</tr>
              <tr className="border-b"><td className="p-3 bg-gray-50 font-medium">Verification</td>{products.map((p) => <td key={p.id} className="p-3">{p.gst}</td>)}</tr>
              {Array.from(allKeys).map((k) => (
                <tr key={k} className="border-b">
                  <td className="p-3 bg-gray-50 font-medium">{k}</td>
                  {products.map((p) => <td key={p.id} className="p-3">{p.specs?.[k] || '—'}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ComparePage;
